import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Copy, History, List } from 'lucide-react'
import { useGetAuthzMeQuery } from '@axilon-apps/domains-ontology-ui-state'

import { LazyChatMarkdown } from '../../components/ChatMarkdown/LazyChatMarkdown'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip'
import { cn } from '../../lib/utils'
import {
  normalizeReleaseVersion,
  parseCustomerChangelog,
  RELEASE_VERSION_CORE
} from './customerChangelog'
import styles from './ReleaseHistoryPage.module.css'

type ReleaseKind = 'forward' | 'rollback' | 'baseline'

export interface CustomerRelease {
  id: string
  version: string
  commitSha: string
  deployedAt: string
  kind: ReleaseKind
}

export interface ReleaseHistory {
  schemaVersion: 1
  currentReleaseId: string | null
  releases: CustomerRelease[]
}

const KINDS = new Set<ReleaseKind>(['forward', 'rollback', 'baseline'])
const SEMVER_PATTERN = new RegExp(`^v${RELEASE_VERSION_CORE}$`)

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

function parseRelease(value: unknown): CustomerRelease | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null
  const release = value as Record<string, unknown>
  if (
    !nonEmptyString(release.id) ||
    !nonEmptyString(release.version) ||
    !SEMVER_PATTERN.test(release.version) ||
    !nonEmptyString(release.commitSha) ||
    !/^[0-9a-f]{40}$/i.test(release.commitSha) ||
    !nonEmptyString(release.deployedAt) ||
    Number.isNaN(Date.parse(release.deployedAt)) ||
    !nonEmptyString(release.kind) ||
    !KINDS.has(release.kind as ReleaseKind)
  ) {
    return null
  }
  return {
    id: release.id,
    version: release.version,
    commitSha: release.commitSha.toLowerCase(),
    deployedAt: new Date(release.deployedAt).toISOString(),
    kind: release.kind as ReleaseKind
  }
}

export function parseReleaseHistory(value: unknown): ReleaseHistory {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('release history must be an object')
  }
  const history = value as Record<string, unknown>
  if (history.schemaVersion !== 1 || !Array.isArray(history.releases)) {
    throw new Error('unsupported release history')
  }
  const releases = history.releases.map(parseRelease)
  if (releases.some((release) => release === null)) {
    throw new Error('release history contains an invalid release')
  }
  const currentReleaseId =
    history.currentReleaseId === null || nonEmptyString(history.currentReleaseId)
      ? history.currentReleaseId
      : null
  return { schemaVersion: 1, currentReleaseId, releases: releases as CustomerRelease[] }
}

async function fetchReleaseHistory(signal: AbortSignal): Promise<ReleaseHistory> {
  const response = await fetch('/_release-history.json', {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    signal
  })
  if (!response.ok) throw new Error(`release history returned ${response.status}`)
  return parseReleaseHistory(await response.json())
}

async function fetchCustomerChangelog(signal: AbortSignal): Promise<string> {
  const response = await fetch('/CHANGELOG.md', {
    cache: 'no-store',
    headers: { Accept: 'text/markdown' },
    signal
  })
  if (!response.ok) throw new Error(`customer changelog returned ${response.status}`)
  const markdown = (await response.text()).trim()
  if (markdown === '') throw new Error('customer changelog is empty')
  return markdown
}

function CurrentBadge() {
  return (
    <Badge variant="secondary" className="rounded px-1.5 py-0 text-11 text-primary bg-primary/10">
      Current
    </Badge>
  )
}

export function ReleaseHistoryPage() {
  const { data: authz, isError, isLoading, isFetching } = useGetAuthzMeQuery()
  const canViewDeployments =
    !isError &&
    !isLoading &&
    !isFetching &&
    authz?.roles.some((role) => role === 'admin' || role === 'global_admin') === true
  const [history, setHistory] = useState<ReleaseHistory | null>(null)
  const [historyFailed, setHistoryFailed] = useState(false)
  const [changelog, setChangelog] = useState<string | null>(null)
  const [changelogFailed, setChangelogFailed] = useState(false)
  const [copiedSha, setCopiedSha] = useState<string | null>(null)
  const [copyFailed, setCopyFailed] = useState(false)
  const [tab, setTab] = useState('changelog')
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  const [showAllDeployments, setShowAllDeployments] = useState(false)
  const [missingVersion, setMissingVersion] = useState<string | null>(null)
  const [navigation, setNavigation] = useState<number | null>(null)
  const navigationSequence = useRef(0)
  const releaseNotes = useRef<HTMLElement>(null)
  const sections = useMemo(() => (changelog ? parseCustomerChangelog(changelog) : []), [changelog])
  const current = history?.releases.find((release) => release.id === history.currentReleaseId)
  const currentVersion = current ? normalizeReleaseVersion(current.version) : null
  const currentSection = sections.find(
    (section) => section.version === currentVersion && section.version !== null
  )
  const selectedSection = sections.find(
    (section) => section.id === (selectedSectionId ?? currentSection?.id ?? sections[0]?.id)
  )

  useEffect(() => {
    const controller = new AbortController()
    void fetchReleaseHistory(controller.signal)
      .then(setHistory)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setHistoryFailed(true)
      })
    void fetchCustomerChangelog(controller.signal)
      .then(setChangelog)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setChangelogFailed(true)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!navigation || tab !== 'changelog') return
    let cancelled = false
    const frame = requestAnimationFrame(() => {
      const animations = releaseNotes.current?.getAnimations?.({ subtree: true }) ?? []
      void Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
        if (cancelled) return
        const target = releaseNotes.current
        target?.focus({ preventScroll: true })
        target?.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth'
        })
        setNavigation(null)
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [navigation, tab])

  const copyCommit = async (commitSha: string) => {
    setCopyFailed(false)
    try {
      await navigator.clipboard.writeText(commitSha)
      setCopiedSha(commitSha)
    } catch {
      setCopiedSha(null)
      setCopyFailed(true)
    }
  }

  const openReleaseNotes = (version: string) => {
    const section = sections.find((entry) => entry.version === normalizeReleaseVersion(version))
    setTab('changelog')
    setMissingVersion(section ? null : version)
    setSelectedSectionId(section?.id ?? '')
    setNavigation(section ? ++navigationSequence.current : null)
  }

  const visibleDeployments = showAllDeployments ? history?.releases : history?.releases.slice(0, 5)

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-[68rem] flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <History className="size-5 text-ink-3" aria-hidden="true" />
          <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">Releases</h1>
        </div>
        {current ? (
          <p className="m-0 flex flex-wrap items-center gap-2 text-xs text-ink-3">
            Currently running <span className="font-medium text-ink">{current.version}</span>
          </p>
        ) : null}
      </header>

      <Tabs
        value={canViewDeployments ? tab : 'changelog'}
        onValueChange={(value) => {
          setNavigation(null)
          setTab(value)
        }}
        className="min-w-0 gap-5"
      >
        <TabsList
          variant="line"
          aria-label="Release information"
          className="w-full justify-start gap-4 rounded-none border-b border-line p-0 max-sm:gap-3"
        >
          <TabsTrigger
            value="changelog"
            className="flex-none px-0 pb-3 text-xs sm:text-13 motion-reduce:transition-none"
          >
            <List className="hidden size-3.5 sm:block" aria-hidden="true" /> Changelog
            {sections.filter((section) => section.version).length ? (
              <span className="text-xs text-ink-3">
                {sections.filter((section) => section.version).length}
              </span>
            ) : null}
          </TabsTrigger>
          {canViewDeployments ? (
            <TabsTrigger
              value="deployments"
              className="flex-none px-0 pb-3 text-xs sm:text-13 motion-reduce:transition-none"
            >
              <History className="hidden size-3.5 sm:block" aria-hidden="true" /> Deployment history
              {history ? (
                <span className="text-xs text-ink-3">{history.releases.length}</span>
              ) : null}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="changelog" className="min-w-0">
          <section aria-label="Changelog" className="min-w-0">
            {changelog === null && !changelogFailed ? (
              <p className="text-sm text-ink-3" role="status">
                Loading changelog…
              </p>
            ) : null}
            {changelogFailed ? (
              <p role="alert" className="text-sm text-destructive">
                The changelog is unavailable for this build.
              </p>
            ) : null}
            {changelog ? (
              sections.length ? (
                <div className={styles.navigator}>
                  <nav aria-label="Changelog versions" className={styles.versionNav}>
                    <p className={styles.navLabel}>Versions</p>
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        aria-pressed={selectedSection?.id === section.id}
                        aria-controls="release-notes-panel"
                        className={styles.versionOption}
                        onClick={() => {
                          setSelectedSectionId(section.id)
                          setNavigation(null)
                          setMissingVersion(null)
                        }}
                      >
                        <span className={styles.optionLine}>
                          <span>{section.title}</span>
                          {section.version !== null && section.version === currentVersion ? (
                            <CurrentBadge />
                          ) : null}
                        </span>
                        {section.date ? (
                          <time dateTime={section.date}>
                            {new Date(`${section.date}T12:00:00`).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </time>
                        ) : null}
                      </button>
                    ))}
                  </nav>
                  <section
                    id="release-notes-panel"
                    ref={releaseNotes}
                    aria-labelledby={selectedSection ? 'release-notes-title' : undefined}
                    aria-label={selectedSection ? undefined : 'Release notes'}
                    tabIndex={-1}
                    className={styles.detail}
                  >
                    {missingVersion ? (
                      <p role="status" className="m-0 text-13 text-ink-3">
                        Release notes for {missingVersion} are not available.
                      </p>
                    ) : null}
                    {selectedSection ? (
                      <div key={selectedSection.id} className={styles.detailContent}>
                        <header className={styles.detailHeader}>
                          <h2
                            id="release-notes-title"
                            className="m-0 text-lg font-medium tracking-tight text-ink"
                          >
                            {selectedSection.title}
                          </h2>
                          {selectedSection.version !== null &&
                          selectedSection.version === currentVersion ? (
                            <CurrentBadge />
                          ) : null}
                          {selectedSection.date ? (
                            <time
                              className="ml-auto text-xs text-ink-3"
                              dateTime={selectedSection.date}
                            >
                              {new Date(`${selectedSection.date}T12:00:00`).toLocaleDateString(
                                undefined,
                                { month: 'short', day: 'numeric', year: 'numeric' }
                              )}
                            </time>
                          ) : null}
                        </header>
                        {selectedSection.markdown.trim() ? (
                          <LazyChatMarkdown className={styles.notes}>
                            {selectedSection.markdown}
                          </LazyChatMarkdown>
                        ) : (
                          <p className="m-0 text-13 text-ink-3">
                            No release notes were recorded for this version.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </section>
                </div>
              ) : (
                <>
                  {missingVersion ? (
                    <p role="status" className="mb-3 text-13 text-ink-3">
                      Release notes for {missingVersion} are not available.
                    </p>
                  ) : null}
                  <LazyChatMarkdown className={styles.notes}>{changelog}</LazyChatMarkdown>
                </>
              )
            ) : null}
          </section>
        </TabsContent>

        {canViewDeployments ? (
          <TabsContent value="deployments" className="min-w-0">
            <section aria-label="Deployment history" className="min-w-0">
              {history === null && !historyFailed ? (
                <p className="text-sm text-ink-3" role="status">
                  Loading deployment history…
                </p>
              ) : null}
              {historyFailed ? (
                <p role="alert" className="text-sm text-destructive">
                  Deployment history is unavailable for this build.
                </p>
              ) : null}
              {history?.releases.length === 0 ? (
                <p className="text-sm text-ink-3">No tracked deployments are available yet.</p>
              ) : null}
              {history && history.releases.length > 0 ? (
                <>
                  <div className="overflow-hidden rounded-lg border border-line bg-surface-1">
                    <Table aria-label="Deployment history">
                      <TableHeader className="bg-surface-2/60">
                        <TableRow className="border-line">
                          <TableHead className="h-8 px-3 text-xs font-normal text-ink-3">
                            Version
                          </TableHead>
                          <TableHead className="h-8 px-3 text-xs font-normal text-ink-3">
                            Deployed
                          </TableHead>
                          <TableHead className="h-8 px-3 text-xs font-normal text-ink-3">
                            Commit
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleDeployments?.map((release) => (
                          <TableRow
                            key={release.id}
                            className={cn(
                              'border-line',
                              release.id === history.currentReleaseId && 'bg-primary/5'
                            )}
                          >
                            <TableCell className="px-3 py-2 text-13">
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  className="text-primary underline decoration-primary/40 underline-offset-3 hover:decoration-primary focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
                                  aria-label={`View changelog for ${release.version}`}
                                  disabled={changelog === null && !changelogFailed}
                                  onClick={() => openReleaseNotes(release.version)}
                                >
                                  {release.version}
                                </button>
                                {release.id === history.currentReleaseId ? <CurrentBadge /> : null}
                                {release.kind === 'rollback' ? (
                                  <Badge
                                    variant="secondary"
                                    className="rounded px-1.5 py-0 text-11"
                                  >
                                    Rollback
                                  </Badge>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="px-3 py-2 text-13 tabular-nums">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <time
                                    dateTime={release.deployedAt}
                                    tabIndex={0}
                                    className="whitespace-nowrap"
                                  >
                                    {new Date(release.deployedAt).toLocaleDateString(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                    <span className="ml-2 text-xs text-ink-3 max-sm:ml-0 max-sm:block">
                                      {new Date(release.deployedAt).toLocaleTimeString(undefined, {
                                        hour: 'numeric',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </time>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {new Date(release.deployedAt).toLocaleString()}
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <code className="text-xs text-ink-3" tabIndex={0}>
                                      {release.commitSha.slice(0, 7)}
                                    </code>
                                  </TooltipTrigger>
                                  <TooltipContent className="break-all font-mono">
                                    {release.commitSha}
                                  </TooltipContent>
                                </Tooltip>
                                <Button
                                  type="button"
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`Copy commit ${release.commitSha}`}
                                  onClick={() => void copyCommit(release.commitSha)}
                                >
                                  {copiedSha === release.commitSha ? (
                                    <Check className="size-3.5" />
                                  ) : (
                                    <Copy className="size-3.5" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {history.releases.length > 5 ? (
                    <div className="mt-2 flex items-center justify-between gap-3 text-xs text-ink-3">
                      <span>
                        {visibleDeployments?.length} of {history.releases.length} deployments
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-ink-3"
                        aria-expanded={showAllDeployments}
                        onClick={() => setShowAllDeployments((value) => !value)}
                      >
                        {showAllDeployments ? 'Show recent' : 'Show all deployments'}
                      </Button>
                    </div>
                  ) : null}
                  <p
                    role={copyFailed ? 'alert' : 'status'}
                    className={cn('mt-2 mb-0 text-xs', copyFailed ? 'text-destructive' : 'sr-only')}
                  >
                    {copyFailed
                      ? 'Could not copy the commit. The full hash is available beside the copy button.'
                      : copiedSha
                        ? 'Full commit hash copied.'
                        : ''}
                  </p>
                </>
              ) : null}
            </section>
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  )
}
